import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');

    try {
      const res = await fetch(`${BACKEND_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name }),
      });
      const data = await res.json();

      if (!res.ok) {
        setErrorMsg(
          typeof data.detail === 'string'
            ? data.detail
            : 'Something went wrong. Please try again.'
        );
        return;
      }

      setIsSubmitted(true);
      setEmail('');
      setName('');
    } catch (err) {
      setErrorMsg('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-dark-bg">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-96 h-96 bg-gold/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold text-xs font-medium uppercase tracking-widest mb-4">
            Join 10,000+ Curious Minds
          </p>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-warm-white mb-6 leading-tight tracking-tight">
            Never Miss an Episode
          </h1>
          
          <p className="text-lg text-warm-white/60 mb-16 max-w-2xl mx-auto leading-relaxed">
            Subscribe to our newsletter and get episode notifications, exclusive
            insights, show notes, and bonus content delivered to your inbox
            every Monday morning.
          </p>

          {/* Newsletter Form */}
          {!isSubmitted ? (
            <Card className="max-w-2xl mx-auto border border-dark-border bg-dark-card">
              <CardContent className="p-10">
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="newsletter-form">
                  <div>
                    <Input
                      type="text"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      data-testid="newsletter-name-input"
                      className="h-14 text-lg bg-dark-bg border-dark-border text-warm-white placeholder:text-warm-white/40 focus:border-gold focus:ring-gold"
                    />
                  </div>
                  
                  <div>
                    <Input
                      type="email"
                      placeholder="Your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="newsletter-email-input"
                      className="h-14 text-lg bg-dark-bg border-dark-border text-warm-white placeholder:text-warm-white/40 focus:border-gold focus:ring-gold"
                    />
                  </div>

                  {errorMsg && (
                    <p className="text-sm text-red-400 text-center" data-testid="newsletter-error">
                      {errorMsg}
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="newsletter-submit-button"
                    className="w-full h-14 bg-gold hover:bg-gold/90 text-dark-bg font-semibold text-sm uppercase tracking-wider transition-all duration-300 disabled:opacity-60"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Subscribing...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 w-4 h-4" />
                        Subscribe Now — It's Free
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-warm-white/50 text-center uppercase tracking-wider">
                    By subscribing, you agree to receive our weekly newsletter.
                    Unsubscribe anytime.
                  </p>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card className="max-w-2xl mx-auto border border-gold/30 bg-dark-card" data-testid="newsletter-success">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 bg-gold flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-dark-bg" />
                </div>
                <h3 className="text-2xl font-bold text-warm-white mb-3 tracking-tight">
                  Welcome to the Community
                </h3>
                <p className="text-warm-white/60">
                  Check your inbox to confirm your subscription. Your first
                  newsletter is on its way!
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-dark-card border-y border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-warm-white mb-16 text-center tracking-tight">
            What You'll Get
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="p-6 text-center border border-dark-border bg-dark-bg hover:border-gold/30 transition-all duration-300">
              <div className="w-12 h-12 bg-gold flex items-center justify-center mx-auto mb-4">
                <span className="text-dark-bg font-bold">01</span>
              </div>
              <h3 className="text-lg font-bold text-warm-white mb-2">
                Episode Notifications
              </h3>
              <p className="text-warm-white/60 text-sm">
                Be the first to know when a new episode drops every Monday
              </p>
            </Card>

            <Card className="p-6 text-center border border-dark-border bg-dark-bg hover:border-gold/30 transition-all duration-300">
              <div className="w-12 h-12 bg-gold flex items-center justify-center mx-auto mb-4">
                <span className="text-dark-bg font-bold">02</span>
              </div>
              <h3 className="text-lg font-bold text-warm-white mb-2">
                Detailed Show Notes
              </h3>
              <p className="text-warm-white/60 text-sm">
                Key takeaways, resources, and links mentioned in each episode
              </p>
            </Card>

            <Card className="p-6 text-center border border-dark-border bg-dark-bg hover:border-gold/30 transition-all duration-300">
              <div className="w-12 h-12 bg-gold flex items-center justify-center mx-auto mb-4">
                <span className="text-dark-bg font-bold">03</span>
              </div>
              <h3 className="text-lg font-bold text-warm-white mb-2">
                Exclusive Content
              </h3>
              <p className="text-warm-white/60 text-sm">
                Bonus insights, behind-the-scenes, and subscriber-only content
              </p>
            </Card>

            <Card className="p-6 text-center border border-dark-border bg-dark-bg hover:border-gold/30 transition-all duration-300">
              <div className="w-12 h-12 bg-gold flex items-center justify-center mx-auto mb-4">
                <span className="text-dark-bg font-bold">04</span>
              </div>
              <h3 className="text-lg font-bold text-warm-white mb-2">
                Free Resources
              </h3>
              <p className="text-warm-white/60 text-sm">
                Worksheets, templates, and tools to apply what you learn
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 bg-dark-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-12 mb-12">
              <div>
                <div className="text-4xl font-bold text-warm-white">10K+</div>
                <div className="text-warm-white/50 text-xs uppercase tracking-widest mt-1">Subscribers</div>
              </div>
              <div className="w-px h-16 bg-dark-border"></div>
              <div>
                <div className="text-4xl font-bold text-warm-white">50+</div>
                <div className="text-warm-white/50 text-xs uppercase tracking-widest mt-1">Episodes</div>
              </div>
              <div className="w-px h-16 bg-dark-border"></div>
              <div>
                <div className="text-4xl font-bold text-warm-white">4.9</div>
                <div className="text-warm-white/50 text-xs uppercase tracking-widest mt-1">Rating</div>
              </div>
            </div>

            <div className="max-w-2xl mx-auto">
              <p className="text-lg text-warm-white/70 italic mb-4 leading-relaxed">
                "The newsletter is just as valuable as the podcast itself. The
                show notes and resources have become my Monday morning
                must-read."
              </p>
              <p className="text-gold text-sm uppercase tracking-widest">
                — Sarah M., Product Manager
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Newsletter;
